// killcam.ts — FACE & FURY STRETCH (#16): the last-3s route-replay KILLCAM.
//
// The share used to be a static PNG; the killcam is the CLIP. On a rival / photo-finish / perfect
// Result it replays the FINAL ~3 seconds of the match — the muncher's route, its chomps, its threads,
// and the winning/losing beat — inside a broadcast "FINAL SECONDS" frame, then offers a WebM export.
//
// DETERMINISM CONTRACT (STRETCH B4): the replay is a PURE CONSUMER of the event-log tail. The model is
// a pure function of (events, snapshot) and every drawn frame is a pure function of (model, progress) —
// so byte-identical logs render byte-identical replays. The only non-deterministic inputs are the
// PRESENTATION clock (a Phaser tween / performance.now → a normalized progress) and the platform
// capture APIs; those never change frame CONTENT. NO Date.now / Math.random anywhere in this file.
//
// GRACEFUL DEGRADATION: MediaRecorder + canvas.captureStream are platform APIs. Missing support (or any
// failure) returns null and the caller falls back to the still route card — never a broken button. The
// single offscreen canvas draw path is shared by the on-screen overlay (mounted as a Phaser canvas
// texture) and the WebM recorder, so what you watch is exactly what you export.
//
// Renderer-only: Phaser + PALETTE + the pure share helpers. No Core / determinism / data touch.

import Phaser from 'phaser';
import type { GameEvent, Snapshot } from '../core';
import { PALETTE, hex, FONT } from './keys';
import { isLandscape } from './stage';
import { buildDeepLink, buildShareText, type ShareCardData, type ShareOutcome } from './share';

// ── Tunables (config, not inline magic) ──────────────────────────────────────────────────────────
export const KILLCAM = {
  /** length of the event-log tail replayed (the "final seconds"). */
  windowMs: 3000,
  /** on-screen presentation duration of one play-through (tween clock, presentation-only). */
  playMs: 1900,
  /** the last frame is held this long (verdict reads) before the overlay dismisses / the recorder stops. */
  holdMs: 350,
  /** fewer than this many player-0 route steps in the tail → not worth a killcam (degrade to nothing). */
  minSteps: 3,
  /** WebM capture frame rate + a hard cap on record wall-time. */
  webmFps: 30,
  webmMaxMs: 2600,
  webmBitsPerSec: 4_000_000,
  /** offscreen canvas the frame is drawn into (portrait-friendly mini clip). */
  canvasW: 540,
  canvasH: 640,
  /** beat pop lifetime (ms of sim time). */
  beatLifeMs: 460,
  // LANDSCAPE fit band. The card is a PORTRAIT aspect; on the landscape stage the naive height-bound fit
  // made a near-full-height card whose below-card TAP-TO-SKIP hint collided with the Result dock (the
  // NEXT BOARD primary CTA + the SHARE/AGAIN/RANKS trio). Instead the card is fitted into the band ABOVE
  // that dock and biased upward, so the hint clears every control. Portrait keeps its centred 0.82 fit.
  landscapeTopSafe: 62, // px below the REPLAY / REPORT chips the card top may reach
  landscapeHintRoom: 44, // px reserved between the card bottom and the Result dock for the skip hint
} as const;

// ── Model (pure) ─────────────────────────────────────────────────────────────────────────────────
export interface KillcamCell {
  id: number;
  row: number;
  col: number;
  kind: 'correct' | 'decoy' | 'wall';
  /** 0 = human ate it, 1 = rival ate it, null = nobody. */
  eatenBy: number | null;
  /** a decoy routed THROUGH (a free Gauntlet thread), never eaten. */
  armed: boolean;
}

/** One point on a player's route: they occupied `cell` at sim time `t`. */
export interface KcStep {
  t: number;
  cell: number;
}

export type KcBeatKind = 'chomp' | 'decoy' | 'thread' | 'win' | 'loss' | 'perfect';
export interface KcBeat {
  t: number;
  cell: number;
  kind: KcBeatKind;
}

export type KcVerdict = 'win' | 'loss' | 'photo' | 'perfect';

export interface KillcamModel {
  rows: number;
  cols: number;
  cells: KillcamCell[];
  /** tail window [startT, endT] in sim ms (endT = last event). */
  startT: number;
  endT: number;
  durationMs: number;
  /** the human (player 0) route through the tail. */
  human: KcStep[];
  /** the rival (player 1) route through the tail, or [] in solo/perfect. */
  rival: KcStep[];
  /** the human's signature beats (chomps / decoys / threads) + the closing verdict beat. */
  beats: KcBeat[];
  verdict: KcVerdict;
  /** photo-finish score margin (only meaningful when verdict === 'photo'). */
  margin?: number;
  /** topic label for the broadcast lower-third. */
  label: string;
}

export interface KillcamVerdictInput {
  won: boolean;
  photoFinish: boolean;
  perfect: boolean;
  margin?: number;
  label: string;
}

/** Sorted-by-t walk: the cell a player occupied at (or last before) time `t`, or null if none is known. */
function seedCellBefore(moves: readonly GameEvent[], t: number): number | null {
  let cell: number | null = null;
  for (const e of moves) {
    if (e.t <= t) cell = e.cell ?? cell;
    else break;
  }
  return cell;
}

function routeTimeAtProgress(steps: readonly KcStep[], progress: number, fallback: number): number {
  if (steps.length === 0) return fallback;
  if (steps.length === 1) return steps[0].t;
  const p = Math.max(0, Math.min(1, progress));
  const scaled = p * (steps.length - 1);
  const i = Math.min(steps.length - 2, Math.floor(scaled));
  const f = scaled - i;
  return steps[i].t + (steps[i + 1].t - steps[i].t) * f;
}

/**
 * Build the killcam model from a finished board's event log + its final snapshot. PURE: no clocks, no
 * RNG. Returns null when the tail is too thin to be worth a clip (fail-closed → the caller shows nothing
 * and the still share card is untouched). `events` is expected in log (time) order.
 */
export function buildKillcamModel(
  events: readonly GameEvent[] | undefined,
  snap: Snapshot,
  v: KillcamVerdictInput,
): KillcamModel | null {
  if (!events || events.length === 0) return null;
  const endT = events[events.length - 1].t;
  const startT = Math.max(0, endT - KILLCAM.windowMs);
  if (!(endT > startT)) return null;

  const cells: KillcamCell[] = snap.cells.map((c) => ({
    id: c.id,
    row: c.row,
    col: c.col,
    kind: c.kind,
    eatenBy: c.eatenBy,
    armed: c.armed,
  }));

  const route = (player: number): KcStep[] => {
    const moves = events.filter((e) => e.type === 'move' && e.player === player);
    const steps: KcStep[] = [];
    const seed = seedCellBefore(moves, startT);
    for (const e of moves) {
      if (e.t > startT && e.t <= endT && e.cell != null) steps.push({ t: e.t, cell: e.cell });
    }
    // Seat the muncher where it actually WAS at tail-start (the last move before the window) so the
    // route starts from the true position, not cell 0 — and never mid-air.
    if (steps.length > 0) {
      const first = steps[0];
      const seatCell = seed ?? first.cell;
      if (steps[0].t > startT) steps.unshift({ t: startT, cell: seatCell });
    } else if (seed != null) {
      // No moves in the tail — the muncher stood still. Hold its seat for the whole window.
      steps.push({ t: startT, cell: seed }, { t: endT, cell: seed });
    }
    return steps;
  };

  const human = route(0);
  const rival = route(1);
  if (human.length < KILLCAM.minSteps) return null;

  const beats: KcBeat[] = [];
  for (const e of events) {
    if (e.t <= startT || e.t > endT || e.player !== 0 || e.cell == null) continue;
    if (e.type === 'munchCorrect') beats.push({ t: e.t, cell: e.cell, kind: 'chomp' });
    else if (e.type === 'munchDecoy') beats.push({ t: e.t, cell: e.cell, kind: 'decoy' });
    else if (e.type === 'gauntlet') beats.push({ t: e.t, cell: e.cell, kind: 'thread' });
  }

  const verdict: KcVerdict = v.perfect ? 'perfect' : v.photoFinish ? 'photo' : v.won ? 'win' : 'loss';
  // Closing verdict beat on the human's final cell (the beat the whole clip builds to).
  const endCell = human[human.length - 1]?.cell ?? 0;
  beats.push({ t: endT, cell: endCell, kind: verdict === 'loss' ? 'loss' : verdict === 'perfect' ? 'perfect' : 'win' });

  return {
    rows: snap.rows,
    cols: snap.cols,
    cells,
    startT,
    endT,
    durationMs: endT - startT,
    human,
    rival,
    beats,
    verdict,
    margin: v.margin,
    label: v.label,
  };
}

// ── Frame geometry + pure drawing ────────────────────────────────────────────────────────────────

interface BoardLayout {
  ox: number;
  oy: number;
  cell: number;
  cols: number;
  byId: Map<number, KillcamCell>;
}

function layoutFor(model: KillcamModel): BoardLayout {
  const pad = 34;
  const bandTop = 108;
  const bandBottom = KILLCAM.canvasH - 116;
  const availW = KILLCAM.canvasW - pad * 2;
  const availH = bandBottom - bandTop;
  const cell = Math.floor(Math.min(availW / model.cols, availH / model.rows));
  const boardW = cell * model.cols;
  const boardH = cell * model.rows;
  const ox = (KILLCAM.canvasW - boardW) / 2;
  const oy = bandTop + (availH - boardH) / 2;
  const byId = new Map<number, KillcamCell>();
  for (const c of model.cells) byId.set(c.id, c);
  return { ox, oy, cell, cols: model.cols, byId };
}

function centerOf(L: BoardLayout, id: number): { x: number; y: number } | null {
  const c = L.byId.get(id);
  if (!c) return null;
  return { x: L.ox + c.col * L.cell + L.cell / 2, y: L.oy + c.row * L.cell + L.cell / 2 };
}

/** Interpolated position along a route at sim time `t` (grid glide between adjacent cell centers). */
function posAt(L: BoardLayout, steps: KcStep[], t: number): { x: number; y: number } | null {
  if (steps.length === 0) return null;
  if (t <= steps[0].t) return centerOf(L, steps[0].cell);
  const last = steps[steps.length - 1];
  if (t >= last.t) return centerOf(L, last.cell);
  for (let i = 0; i < steps.length - 1; i++) {
    const a = steps[i];
    const b = steps[i + 1];
    if (t >= a.t && t <= b.t) {
      const ca = centerOf(L, a.cell);
      const cb = centerOf(L, b.cell);
      if (!ca || !cb) return ca ?? cb;
      const dt = b.t - a.t;
      const f = dt > 0 ? (t - a.t) / dt : 1;
      return { x: ca.x + (cb.x - ca.x) * f, y: ca.y + (cb.y - ca.y) * f };
    }
  }
  return centerOf(L, last.cell);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Draw a small chomp-creature token in the SHIPPED game identity (not a bare Pac wedge). The human echoes
 * drawPlayerMuncher / the in-board token — a rounded cyan body with a LEFT jaw notch, a big friendly white
 * eye upper-right, and two stubby feet: the eye + feet are exactly what the asset-qa pass added game-wide
 * (AGENTS "Muncher identity made original game-wide") to distance the muncher from a lookalike, and the
 * killcam is the most share-facing surface. The rival keeps its shipped magenta arrowhead identity
 * (drawRivalMuncher). PURE in its args (no clock / RNG) so drawKillcamFrame stays byte-identical.
 */
function drawToken(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, kind: 'human' | 'rival'): void {
  const fill = kind === 'human' ? hex(PALETTE.player) : hex(PALETTE.rival);
  ctx.save();
  ctx.shadowColor = fill;
  ctx.shadowBlur = r * 0.9;
  if (kind === 'rival') {
    // Bare magenta arrowhead (apex right) — the rival's shipped geometric identity (drawRivalMuncher).
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(x + r * 1.02, y);
    ctx.lineTo(x - r * 0.76, y - r * 0.9);
    ctx.lineTo(x - r * 0.76, y + r * 0.9);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    return;
  }
  // Human chomp CREATURE. Feet first (drawn behind the body so only the rounded tips peek out below).
  const m = Math.PI * (40 / 180); // mouth half-angle; the jaw notch gap is centred on the LEFT (angle PI)
  ctx.fillStyle = fill;
  const footR = r * 0.22;
  const footY = y + r * 0.86;
  for (const fx of [-r * 0.42, r * 0.42]) {
    ctx.beginPath();
    ctx.arc(x + fx, footY, footR, 0, Math.PI * 2);
    ctx.fill();
  }
  // Body: a rounded chomp wedge — the full disc minus the left mouth gap (arc sweeps the long way round).
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI + m, Math.PI - m, false);
  ctx.lineTo(x, y);
  ctx.closePath();
  ctx.fill();
  // Big friendly eye upper-right (white + dark pupil + catch-light) — the key original-creature tell.
  ctx.shadowBlur = 0;
  const eyeX = x + r * 0.34;
  const eyeY = y - r * 0.4;
  const eyeR = r * 0.3;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(eyeX, eyeY, eyeR, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#0a0b12';
  ctx.beginPath();
  ctx.arc(eyeX - eyeR * 0.15, eyeY + eyeR * 0.05, eyeR * 0.52, 0, Math.PI * 2); // pupil glances toward the mouth
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(eyeX + eyeR * 0.14, eyeY - eyeR * 0.32, eyeR * 0.2, 0, Math.PI * 2); // catch-light
  ctx.fill();
  ctx.restore();
}

/**
 * Draw ONE killcam frame at normalized `progress` ∈ [0,1]. PURE in (model, progress, trace): the same
 * inputs always paint the same pixels. `trace` draws the FULL route polyline + every beat (the static
 * reduced-motion / still frame); otherwise only the traversed path + fired beats up to `progress`.
 */
export function drawKillcamFrame(
  ctx: CanvasRenderingContext2D,
  model: KillcamModel,
  progress: number,
  opts: { trace?: boolean } = {},
): void {
  const W = KILLCAM.canvasW;
  const H = KILLCAM.canvasH;
  const p = Math.max(0, Math.min(1, progress));
  const simT = model.startT + p * model.durationMs;
  // Presentation spreads the visible route over most of the overlay. The event-log tail can be dense
  // in its first half, which made the "Final Seconds" path finish too quickly even with a long tween.
  const routeP = opts.trace ? 1 : Math.max(0, Math.min(1, p / 0.9));
  const humanT = opts.trace ? model.endT : routeTimeAtProgress(model.human, routeP, simT);
  const rivalT = opts.trace ? model.endT : routeTimeAtProgress(model.rival, routeP, simT);
  const L = layoutFor(model);
  const goldVerdict = model.verdict !== 'loss';
  const accent = goldVerdict ? PALETTE.gold : PALETTE.hazard;

  // background
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, hex(PALETTE.bg));
  grad.addColorStop(1, '#0a0c14');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // broadcast frame
  ctx.strokeStyle = hex(accent);
  ctx.lineWidth = 8;
  ctx.strokeRect(14, 14, W - 28, H - 28);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // top lower-third
  ctx.fillStyle = hex(accent);
  ctx.font = `bold 40px ${FONT.display}`;
  ctx.fillText('FINAL SECONDS', W / 2, 56);
  ctx.fillStyle = hex(PALETTE.textDim);
  ctx.font = `22px ${FONT.family}`;
  ctx.fillText(model.label.toUpperCase(), W / 2, 88);

  // board cells
  const gap = Math.max(2, Math.round(L.cell * 0.08));
  for (const c of model.cells) {
    const x = L.ox + c.col * L.cell + gap / 2;
    const y = L.oy + c.row * L.cell + gap / 2;
    const s = L.cell - gap;
    let fill = '';
    let stroke = hex(PALETTE.line);
    if (c.kind === 'wall') {
      fill = hex(PALETTE.surface2);
    } else if (c.eatenBy === 0 && c.kind === 'correct') {
      fill = hex(PALETTE.correct);
      stroke = hex(PALETTE.correct);
    } else if (c.eatenBy === 0 && c.kind === 'decoy') {
      fill = hex(PALETTE.hazard);
      stroke = hex(PALETTE.hazard);
    } else if (c.armed && c.kind === 'decoy') {
      // a threaded (routed-through) decoy — the signature gold pass
      stroke = hex(PALETTE.armed);
    } else if (c.eatenBy === 1) {
      fill = hex(PALETTE.rival);
    }
    roundRect(ctx, x, y, s, s, Math.max(4, s * 0.16));
    if (fill) {
      ctx.globalAlpha = c.eatenBy === 1 ? 0.32 : 0.9;
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    ctx.lineWidth = c.armed && c.kind === 'decoy' ? 3 : 1.4;
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }

  // route polyline (human) — full faint trace + a bright traversed head
  const drawPath = (steps: KcStep[], upto: number, color: string, width: number, alpha: number): void => {
    if (steps.length < 2) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = width;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = width * 1.6;
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      const c = centerOf(L, s.cell);
      if (!c) continue;
      if (s.t > upto) {
        // draw the partial segment into the current head, then stop
        const prev = steps[i - 1];
        const pc = prev ? centerOf(L, prev.cell) : null;
        if (started && pc && prev) {
          const dt = s.t - prev.t;
          const f = dt > 0 ? (upto - prev.t) / dt : 1;
          ctx.lineTo(pc.x + (c.x - pc.x) * f, pc.y + (c.y - pc.y) * f);
        }
        break;
      }
      if (!started) {
        ctx.moveTo(c.x, c.y);
        started = true;
      } else {
        ctx.lineTo(c.x, c.y);
      }
    }
    ctx.stroke();
    ctx.restore();
  };

  const traversedTo = opts.trace ? model.endT : humanT;
  // faint full trace underlay (only in trace/still mode; keeps the animated head clean)
  if (opts.trace) drawPath(model.human, model.endT, hex(PALETTE.player), Math.max(3, L.cell * 0.1), 0.28);
  drawPath(model.human, traversedTo, hex(PALETTE.player), Math.max(4, L.cell * 0.14), 0.95);

  // beat pops (chomps / threads) up to now
  for (const b of model.beats) {
    if (b.kind === 'win' || b.kind === 'loss' || b.kind === 'perfect') continue;
    const age = (opts.trace ? model.endT : humanT) - b.t;
    const c = centerOf(L, b.cell);
    if (!c) continue;
    if (opts.trace) {
      // static markers on the still frame
      ctx.strokeStyle = b.kind === 'decoy' ? hex(PALETTE.hazard) : b.kind === 'thread' ? hex(PALETTE.armed) : hex(PALETTE.correct);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(c.x, c.y, L.cell * 0.5, 0, Math.PI * 2);
      ctx.stroke();
      continue;
    }
    if (age < 0 || age > KILLCAM.beatLifeMs) continue;
    const f = age / KILLCAM.beatLifeMs;
    ctx.save();
    ctx.globalAlpha = 1 - f;
    ctx.strokeStyle = b.kind === 'decoy' ? hex(PALETTE.hazard) : b.kind === 'thread' ? hex(PALETTE.armed) : hex(PALETTE.correct);
    ctx.lineWidth = 4 * (1 - f) + 1;
    ctx.beginPath();
    ctx.arc(c.x, c.y, L.cell * (0.32 + f * 0.7), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // tokens
  const rivalPos = model.rival.length ? posAt(L, model.rival, rivalT) : null;
  if (rivalPos) drawToken(ctx, rivalPos.x, rivalPos.y, L.cell * 0.3, 'rival');
  const humanPos = posAt(L, model.human, humanT);
  if (humanPos) drawToken(ctx, humanPos.x, humanPos.y, L.cell * 0.34, 'human');

  // verdict lower-third — resolves in the final stretch (or always, on the still)
  const showVerdict = opts.trace || p > 0.86;
  if (showVerdict) {
    const tag =
      model.verdict === 'perfect'
        ? 'PERFECT GAUNTLET'
        : model.verdict === 'photo'
          ? `PHOTO FINISH${model.margin != null ? ` · ${model.margin}` : ''}`
          : model.verdict === 'win'
            ? 'THREADED IT'
            : 'RIVAL TOOK IT';
    const vy = H - 62;
    ctx.save();
    if (!opts.trace) {
      const pop = Math.min(1, (p - 0.86) / 0.1);
      ctx.globalAlpha = pop;
    }
    ctx.fillStyle = hex(accent);
    ctx.font = `bold 42px ${FONT.display}`;
    ctx.shadowColor = hex(accent);
    ctx.shadowBlur = 16;
    ctx.fillText(tag, W / 2, vy);
    ctx.restore();
  }
}

// ── On-screen overlay (Phaser canvas texture) ────────────────────────────────────────────────────

export interface KillcamHandle {
  destroy(): void;
}

/**
 * Play (or, under reducedMotion, statically show) the killcam as a scrimmed overlay in `scene`. Mounts
 * the shared offscreen canvas as a Phaser canvas texture and drives `progress` from a presentation tween
 * (motion) or paints a single trace frame (reducedMotion). Tap dismisses; `onDone` fires once on teardown.
 */
export function playKillcam(
  scene: Phaser.Scene,
  model: KillcamModel,
  opts: { reducedMotion?: boolean; onDone?: () => void; onSkip?: () => void } = {},
): KillcamHandle {
  const W = scene.scale.width;
  const H = scene.scale.height;
  const depth = 20_000;
  let torn = false;

  const scrim = scene.add
    .rectangle(W / 2, H / 2, W, H, 0x05060c, 0.82)
    .setScrollFactor(0)
    .setDepth(depth)
    .setInteractive();

  const canvas = document.createElement('canvas');
  canvas.width = KILLCAM.canvasW;
  canvas.height = KILLCAM.canvasH;
  const ctx = canvas.getContext('2d');
  const texKey = `killcam-${Math.floor(scene.time.now)}-${scene.children.length}`;
  if (scene.textures.exists(texKey)) scene.textures.remove(texKey);
  const tex = ctx ? scene.textures.addCanvas(texKey, canvas) : null;

  // Fit the PORTRAIT-aspect card, keeping aspect. Portrait: centred, height-bound at 0.82 (unchanged).
  // LANDSCAPE: the Result dock (NEXT BOARD primary CTA + SHARE/AGAIN/RANKS trio) sits at the bottom, so
  // fit the card into the band ABOVE it and bias it UP — the below-card TAP-TO-SKIP hint then clears every
  // Result control (it used to overlap SHARE / NEXT BOARD). `cardCy` is the card's vertical centre.
  const W2 = W / 2;
  const fitW = (W * 0.9) / KILLCAM.canvasW;
  let scale: number;
  let cardCy: number;
  if (isLandscape()) {
    const dockTop = H - (Math.max(52, H * 0.055) + 44 / 2 + 88); // mirrors ResultScene primary-CTA top
    const bandBottom = dockTop - KILLCAM.landscapeHintRoom;
    scale = Math.min(fitW, (bandBottom - KILLCAM.landscapeTopSafe) / KILLCAM.canvasH);
    cardCy = KILLCAM.landscapeTopSafe + (KILLCAM.canvasH * scale) / 2;
  } else {
    scale = Math.min(fitW, (H * 0.82) / KILLCAM.canvasH);
    cardCy = H / 2;
  }
  const img = scene.add
    .image(W2, cardCy, texKey)
    .setDepth(depth + 1)
    .setScale(scale);

  const cardHalfH = (KILLCAM.canvasH * scale) / 2;
  const skipHintY = isLandscape() ? cardCy + cardHalfH + 22 : cardCy + cardHalfH - Math.max(18, H * 0.022);
  const skipHint = scene.add
    .text(W2, skipHintY, 'TAP TO SKIP', {
      fontFamily: FONT.display,
      fontSize: '13px',
      color: hex(PALETTE.dim),
      fontStyle: '600',
    })
    .setOrigin(0.5)
    .setDepth(depth + 1);

  let tween: Phaser.Tweens.Tween | undefined;
  let holdTimer: Phaser.Time.TimerEvent | undefined;

  const paint = (progress: number, trace: boolean): void => {
    if (!ctx || !tex) return;
    drawKillcamFrame(ctx, model, progress, { trace });
    tex.refresh();
  };

  const teardown = (skipped = false): void => {
    if (torn) return;
    torn = true;
    if (skipped) opts.onSkip?.();
    tween?.stop();
    holdTimer?.remove(false);
    scrim.off('pointerdown', onTap);
    scene.tweens.add({
      targets: [img, scrim, skipHint],
      alpha: 0,
      duration: 120,
      onComplete: () => {
        img.destroy();
        scrim.destroy();
        skipHint.destroy();
        if (scene.textures.exists(texKey)) scene.textures.remove(texKey);
        // Release the caller's modal guard only AFTER the fade — Phaser emits a GameObject's pointerdown
        // (this scrim's skip tap) BEFORE the scene-level pointerdown, so clearing it synchronously would
        // let the same tap fall through to the result's tap-to-advance. Deferring past the event is safe.
        opts.onDone?.();
      },
    });
  };

  const onTap = (): void => teardown(true);
  scrim.on('pointerdown', onTap);

  if (opts.reducedMotion || !ctx || !tex) {
    // static final-frame with the full route trace; waits for a tap (no motion, no auto-timer).
    paint(1, true);
    skipHint.setText('TAP TO CLOSE');
  } else {
    const proxy = { p: 0 };
    paint(0, false);
    tween = scene.tweens.add({
      targets: proxy,
      p: 1,
      duration: KILLCAM.playMs,
      ease: 'Linear',
      onUpdate: () => paint(proxy.p, false),
      onComplete: () => {
        paint(1, false);
        holdTimer = scene.time.delayedCall(KILLCAM.holdMs, () => teardown(false));
      },
    });
  }

  // safety: tear down cleanly if the scene shuts down mid-play (no leaked canvas texture).
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    tween?.stop();
    holdTimer?.remove(false);
    if (scene.textures.exists(texKey)) scene.textures.remove(texKey);
  });

  return { destroy: () => teardown(false) };
}

// ── WebM export (B2) ─────────────────────────────────────────────────────────────────────────────

const WEBM_MIME_CANDIDATES = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'] as const;

/** True iff this platform can capture a canvas to a WebM (MediaRecorder + captureStream + webm). */
export function killcamWebMSupported(): boolean {
  try {
    if (typeof MediaRecorder === 'undefined') return false;
    const c = document.createElement('canvas');
    if (typeof c.captureStream !== 'function') return false;
    if (typeof MediaRecorder.isTypeSupported !== 'function') return false;
    return WEBM_MIME_CANDIDATES.some((m) => MediaRecorder.isTypeSupported(m));
  } catch {
    return false;
  }
}

export interface KillcamVideo {
  blob: Blob;
  mime: string;
  w: number;
  h: number;
}

/**
 * Render the killcam to an offscreen canvas and capture it to a WebM blob. Returns null on absent support
 * or ANY failure (the caller then ships the still card — never a broken affordance). The frame CONTENT is
 * the pure drawKillcamFrame; only the rAF/performance.now DRIVER is wall-clock (presentation timing).
 */
export async function recordKillcamWebM(model: KillcamModel): Promise<KillcamVideo | null> {
  if (!killcamWebMSupported()) return null;
  const canvas = document.createElement('canvas');
  canvas.width = KILLCAM.canvasW;
  canvas.height = KILLCAM.canvasH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // Declared out here so `finally` can ALWAYS release the capture stream + recorder — even when the
  // MediaRecorder construction, rec.start(), or a frame draw throws AFTER captureStream() (else those
  // live tracks leak: the old catch returned null without ever stopping them).
  let stream: MediaStream | null = null;
  let rec: MediaRecorder | null = null;
  try {
    const mime = WEBM_MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? 'video/webm';
    stream = canvas.captureStream(KILLCAM.webmFps);
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: KILLCAM.webmBitsPerSec });
    rec = recorder;
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e): void => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    const stopped = new Promise<void>((res) => {
      recorder.onstop = (): void => res();
    });

    // paint frame 0 before starting so the first captured frame is never blank.
    drawKillcamFrame(ctx, model, 0, {});
    recorder.start();

    const total = Math.min(KILLCAM.webmMaxMs, KILLCAM.playMs);
    const t0 = performance.now();
    await new Promise<void>((resolve) => {
      const step = (): void => {
        const elapsed = performance.now() - t0;
        const prog = Math.min(1, elapsed / total);
        drawKillcamFrame(ctx, model, prog, {});
        if (prog >= 1) {
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });

    // hold the verdict frame a beat, then stop.
    drawKillcamFrame(ctx, model, 1, {});
    await new Promise<void>((res) => {
      setTimeout(res, KILLCAM.holdMs);
    });
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;
    if (chunks.length === 0) return null;
    return { blob: new Blob(chunks, { type: mime }), mime, w: canvas.width, h: canvas.height };
  } catch {
    return null;
  } finally {
    // Release everything exactly once: on the success path the recorder is already 'inactive' and this
    // just stops the tracks; on ANY throw after captureStream() it stops the recorder AND the leaked
    // tracks. Keeps the resolve-null / never-throw contract (nothing in here can escape the finally).
    try {
      if (rec && rec.state !== 'inactive') rec.stop();
    } catch {
      /* recorder already inactive / errored — nothing to release here */
    }
    stream?.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Share the killcam as a WebM: record → Web Share API with the file (mobile native sheet) → download the
 * webm → (if recording/sharing is impossible) fall back to the still route card via the injected sharer.
 * The injected `stillFallback` keeps this module free of a share.ts→killcam cycle.
 */
export async function shareKillcamVideo(
  model: KillcamModel,
  data: ShareCardData,
  stillFallback: (data: ShareCardData) => Promise<ShareOutcome>,
): Promise<ShareOutcome> {
  const video = await recordKillcamWebM(model);
  if (!video) return stillFallback(data);

  const link = buildDeepLink(data);
  const title = 'Trivia Munchers';
  const text = buildShareText(data, link);
  const file = new File([video.blob], 'trivia-munchers-killcam.webm', { type: video.mime });

  try {
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
      share?: (d: ShareData) => Promise<void>;
    };
    if (typeof nav.share === 'function') {
      const shareData: ShareData = { title, text, url: link, files: [file] } as ShareData;
      if (!nav.canShare || nav.canShare(shareData)) {
        await nav.share(shareData);
        return { method: 'shared' };
      }
    }
  } catch (err) {
    // user cancelled the native sheet — a soft no-op, don't fall through to a surprise download.
    if (err instanceof DOMException && err.name === 'AbortError') return { method: 'failed', link };
  }

  // download the webm so the player still gets the clip.
  try {
    const url = URL.createObjectURL(video.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trivia-munchers-killcam.webm';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return { method: 'downloaded', link };
  } catch {
    // last resort — the still card.
    return stillFallback(data);
  }
}
